import React from "react";
import { ASSETS } from "../assetManifest.js";
import { CONSTANTS } from "../data";

interface Customer {
  id: string;
  name: string;
  order: string;
  patience: number;
  maxPatience: number;
  type: "regular" | "kid" | "critic" | "tutorial";
  active: boolean;
  criticOrder?: number;
  preferenceTag?: string;
}

interface CustomerCardProps {
  customer: Customer;
  orderName: string;
  recipeHint: string;
  canServe: boolean;
  onServe: () => void;
  icon: string;
  patienceBarColor: string;
  patiencePercent: number;
  isCritic: boolean;
  isServeDisabled: boolean;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  orderName,
  recipeHint,
  canServe,
  onServe,
  icon,
  patienceBarColor,
  patiencePercent,
  isCritic,
  isServeDisabled,
}) => {
  // Determine mobile sizing
  const isMobile = window.innerWidth < CONSTANTS.MOBILE_UI.MOBILE_BREAKPOINT;
  const isSmallMobile = window.innerWidth < CONSTANTS.MOBILE_UI.SMALL_MOBILE_BREAKPOINT;

  const cardPadding = isSmallMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_PADDING_SMALL_MOBILE 
    : isMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_PADDING_MOBILE 
    : 12; // Default desktop padding

  const iconSize = isSmallMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_ICON_SIZE_SMALL_MOBILE 
    : isMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_ICON_SIZE_MOBILE 
    : 40; // Default desktop size

  const titleSize = isSmallMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_TITLE_SIZE_SMALL_MOBILE 
    : isMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_TITLE_SIZE_MOBILE 
    : 14; // Default desktop size

  const subtitleSize = isSmallMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_SUBTITLE_SIZE_SMALL_MOBILE 
    : isMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_SUBTITLE_SIZE_MOBILE 
    : 12; // Default desktop size

  const serveButtonHeight = isSmallMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_SERVE_BUTTON_HEIGHT_SMALL_MOBILE 
    : isMobile 
    ? CONSTANTS.MOBILE_UI.CUSTOMER_CARD_SERVE_BUTTON_HEIGHT_MOBILE 
    : 36; // Default desktop height

  // Truncate text for mobile
  const displayName = isMobile && customer.name.length > 8 
    ? customer.name.slice(0, 6) + '...' 
    : customer.name;

  const displayOrderName = isMobile && orderName.length > 12 
    ? orderName.slice(0, 10) + '...' 
    : orderName;

  const displayRecipeHint = isMobile && recipeHint.length > 20 
    ? recipeHint.slice(0, 18) + '...' 
    : recipeHint;

  return (
    <div
      className={`customer-card bg-bg-main rounded-lg border border-border-color transition-all mobile-customer-card ${
        isCritic ? "border-accent ring-2 ring-accent/50" : ""
      } ${isServeDisabled ? "opacity-50" : ""}`}
      style={{
        padding: `${cardPadding}px`,
      }}
    >
      {/* Header with customer info and serve button */}
      <div 
        className="flex justify-between items-start"
        style={{
          marginBottom: `${Math.max(4, cardPadding / 2)}px`,
        }}
      >
        <div className="customer-card-container flex items-center flex-1 min-w-0" style={{
          gap: `${Math.max(4, cardPadding / 2)}px`,
        }}>
          <img 
            src={icon} 
            alt={customer.type} 
            className="customer-card-icon rounded-full bg-surface flex-shrink-0" 
            style={{
              width: `${iconSize}px`,
              height: `${iconSize}px`,
              padding: `${Math.max(2, Math.floor(iconSize * 0.1))}px`,
            }}
          />
          <div className="flex-1 min-w-0">
            <div 
              className="font-semibold text-text-primary truncate"
              style={{ fontSize: `${titleSize}px`, lineHeight: 1.2 }}
            >
              {displayName}
            </div>
            <div 
              className="text-text-secondary truncate"
              style={{ fontSize: `${subtitleSize}px`, lineHeight: 1.1 }}
            >
              {isCritic ? `Course ${(customer.criticOrder || 0) + 1}/3: ` : ''}{displayOrderName}
            </div>
          </div>
        </div>
        
        {canServe && !isServeDisabled && (
          <button
            data-tutorial-id={`serve-button-${customer.order}`}
            onClick={onServe}
            className="customer-card-serve-btn bg-success text-white rounded-md font-semibold hover:opacity-90 transition-opacity flex items-center justify-center flex-shrink-0"
            style={{
              height: `${Math.max(serveButtonHeight, CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE)}px`,
              minWidth: `${Math.max(serveButtonHeight, CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE)}px`,
              padding: `0 ${Math.max(6, cardPadding)}px`,
              fontSize: `${Math.max(10, subtitleSize)}px`,
              marginLeft: `${Math.max(4, cardPadding / 2)}px`,
            }}
          >
            {isMobile ? 'Serve' : 'Serve'}
          </button>
        )}
      </div>
      
      {/* Recipe info section - more compact on mobile */}
      <div 
        className="bg-surface rounded-md text-center"
        style={{
          padding: `${Math.max(4, cardPadding / 2)}px`,
          marginBottom: `${Math.max(4, cardPadding / 2)}px`,
        }}
      >
        <p 
          className="text-text-secondary"
          style={{ fontSize: `${Math.max(9, subtitleSize - 1)}px`, lineHeight: 1.2 }}
        >
          {isMobile ? 'Recipe: ' : 'Recipe: '}
          <span className="font-medium text-text-primary">{displayRecipeHint}</span>
        </p>
        {customer.preferenceTag && (
          <p 
            className="text-accent mt-1"
            style={{ fontSize: `${Math.max(8, subtitleSize - 2)}px`, lineHeight: 1.1 }}
          >
            {isMobile ? 'Likes: ' : 'Likes: '}
            <span className="font-semibold capitalize">{customer.preferenceTag}</span>
            {!isMobile && ' (+10% Tip)'}
          </p>
        )}
      </div>
      
      {/* Patience bar */}
      <div 
        className="w-full bg-border-color rounded-full"
        style={{
          height: `${isMobile ? 3 : 4}px`,
        }}
      >
        <div
          className={`rounded-full transition-all duration-300 ${patienceBarColor}`}
          style={{ 
            width: `${patiencePercent}%`,
            height: `${isMobile ? 3 : 4}px`,
          }}
        />
      </div>
    </div>
  );
};

export default React.memo(CustomerCard);
